import { useTranslation } from 'react-i18next'
import { BomImagen } from './BomImagen'
import { fmtQty, renderRich, joinCaracteristicas, CaracAviso } from './fichaRender'
import type { BOMCabecera, BOMLinea, CapturaBOM } from '@/types/bomCaptura'

interface Props {
  itemId: string
  empresa: string
  formula: string
  cabecera?: BOMCabecera
  lineas: BOMLinea[]
  capturas: CapturaBOM[]
}

/** Galería y proceso paginados: bloques de 12 (4 columnas × 3 filas) por hoja. */
const POR_HOJA = 12
function enBloques<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

/** Documento A4 fiel al PDF de referencia (look `.ficha`), pintado con datos en
 *  vivo. Se renderiza oculto en pantalla y solo aparece al imprimir (ver ficha.css). */
export function FichaDocumento({ itemId, empresa, formula, cabecera, lineas, capturas }: Props) {
  const { t } = useTranslation()
  const caracteristicas = capturas.filter((c) => c.tipo === 'caracteristica')
  const manipulacion = capturas.filter((c) => c.tipo === 'manipulacion')
  const caracTexto = joinCaracteristicas(caracteristicas)

  const img = (productnumber?: string, capturaId?: string, className?: string) => (
    <BomImagen itemId={itemId} empresa={empresa} formula={formula}
      productnumber={productnumber} capturaId={capturaId} dark={false} className={className} placeholder={<span />} />
  )

  return (
    <div className="ficha">
      {/* Bloque 1 · Cabecera: logo (20%) + producto / descripción (80%) */}
      <header className="f-header">
        <div className="f-hd__logo"><img src="/logo.svg" alt="AIR-VAL Perfumes" /></div>
        <div className="f-hd__info">
          <div className="f-hd__field">
            <span className="f-hd__label">PRODUCTO</span>
            <span className="f-hd__value">{[itemId, cabecera?.color, cabecera?.estilo].filter(Boolean).join('.')}</span>
          </div>
          <div className="f-hd__field f-hd__field--right">
            <span className="f-hd__label">DESCRIPCIÓN</span>
            <span className="f-hd__value">{cabecera?.nombre || itemId}</span>
          </div>
        </div>
      </header>

      {/* Página 1 · 60/40: [imagen(40%) + características(60%)] · componentes(40%) */}
      <section className="f-doc">
        {/* Bloque izquierdo (60%): imagen + características */}
        <div className="f-doc__main">
          {/* Imagen producto final (40% alto) con su título */}
          <div className="f-doc__prod">
            <div className="f-head-bar">{t('bomCaptura.ficha.productoFinal')}</div>
            {img(itemId, undefined, 'f-doc__prodimg')}
          </div>

          {/* Características de producción (60%) */}
          <div className="f-doc__carac">
            <div className="f-head-bar">{t('bomCaptura.ficha.caracteristicas')}</div>
            <CaracAviso />
            <div className="f-carac__text">{caracTexto ? renderRich(caracTexto) : <p>—</p>}</div>
          </div>
        </div>

        {/* Lista de componentes (40%) */}
        <div className="f-doc__col">
          <div className="f-head-bar">{t('bomCaptura.ficha.componentes')}</div>
          <ol className={`f-clist${lineas.length > 14 ? ' f-clist--2col' : ''}`}>
            {lineas.map((l, i) => (
              <li className="f-clitem" key={l.id || l.itemNumber}>
                <span className="f-clnum">{i + 1}</span>
                <div className="f-clbody">
                  <b className="f-clref">{l.itemNumber}</b>
                  <span className="f-clname">{l.nombre || l.itemNumber}</span>
                </div>
                <span className="f-cluni">{fmtQty(l.quantity)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Página 2+ · Proceso de manipulación, paginado en bloques de 4×3 por hoja */}
      {enBloques(manipulacion, POR_HOJA).map((grupo, gi) => (
        <section className="f-sec f-proceso-page" key={gi}>
          <div className="f-head-bar">{t('bomCaptura.ficha.proceso')}</div>
          <div className="f-proceso__grid f-proceso__grid--doc">
            {grupo.map((p) => (
              <div className="f-step" key={p.id}>
                {img(undefined, p.id, 'f-step__img f-ph')}
                <b>{p.secuencia}. {p.nombre}</b>
                <p>{p.descripcion}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Página 3+ · Galería (orden de armado), paginada en bloques de 4×3 por hoja */}
      {enBloques(lineas, POR_HOJA).map((grupo, gi) => (
        <section className="f-sec f-galeria" key={gi}>
          <div className="f-head-bar">{t('bomCaptura.ficha.componentes')}</div>
          <div className="f-galeria__grid">
            {grupo.map((l, li) => (
              <div className="f-gcard" key={l.id || l.itemNumber}>
                <div className="f-gcard-head">
                  <span className="f-clnum">{gi * POR_HOJA + li + 1}</span>
                  <div className="f-clbody">
                    <b className="f-clref">{[l.itemNumber, l.color, l.estilo].filter(Boolean).join('.')}</b>
                    <span className="f-clname">{l.nombre || l.itemNumber}</span>
                  </div>
                </div>
                {img(l.itemNumber, undefined, 'f-gcard__img f-ph')}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
